import { isWeb } from "@memorang/helpers/platform";
import type { WordLimit } from "@memorang/types";
import { Pressable } from "react-native";
import { Icon, Text } from "react-native-paper";
import type { IconType } from "../../../../icon";
import { useAppTheme } from "../../../../useAppTheme";
import Box from "../../../Box";
import { WordCountChip } from "./WordCountChip";
import type { Selection } from "./helpers";
import { useClipboardActions } from "./useClipboardActions";

export type { Selection } from "./helpers";

const ToolbarButton = ({
	icon,
	label,
	onPress,
	disabled,
	showDivider,
	fullWidth,
}: {
	icon: IconType;
	label: string;
	onPress: () => void;
	disabled?: boolean;
	showDivider?: boolean;
	fullWidth?: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<Pressable
			onPress={onPress}
			disabled={disabled}
			accessibilityRole="button"
			accessibilityLabel={label}
			accessibilityState={{ disabled: Boolean(disabled) }}
			style={({ pressed }) => ({
				flex: fullWidth ? 1 : undefined,
				flexDirection: "row",
				alignItems: "center",
				justifyContent: "center",
				gap: isWeb ? 8 : 4,
				height: 40,
				paddingHorizontal: 16,
				paddingVertical: isWeb ? 8 : 4,
				borderRightWidth: showDivider ? 1 : 0,
				borderRightColor: theme.colors.outline,
				backgroundColor: pressed ? theme.colors.surfaceVariant : "transparent",
				opacity: disabled ? 0.5 : 1,
			})}
		>
			{isWeb ? (
				<div aria-hidden="true" style={{ display: "flex" }}>
					<Icon source={icon} size={24} color={theme.colors.onSurface} />
				</div>
			) : (
				<Icon source={icon} size={18} color={theme.colors.onSurface} />
			)}
			<Text
				variant={isWeb ? "bodyMedium" : "bodySmall"}
				style={{ color: theme.colors.onSurface, fontWeight: "500" }}
			>
				{label}
			</Text>
		</Pressable>
	);
};

type WritingToolbarProps = {
	value?: string;
	onChangeText: (value: string) => void;
	onSelectionChange?: (selection: Selection) => void;
	selection?: Selection;
	maxLength?: number;
	disabled?: boolean;
	wordCount: number;
	pluralWord: string;
	wordLimit?: WordLimit;
	wordCountText?: string;
	withTopBorder?: boolean;
	withBottomBorder?: boolean;
	hideChip?: boolean;
};

export const WritingToolbar = ({
	value,
	onChangeText,
	onSelectionChange,
	selection,
	maxLength,
	disabled,
	wordCount,
	pluralWord,
	wordLimit,
	wordCountText,
	withTopBorder,
	withBottomBorder,
	hideChip,
}: WritingToolbarProps) => {
	const theme = useAppTheme();
	const { handleCopy, handleCut, handlePaste, canCopy, canCut } =
		useClipboardActions({
			value,
			onChangeText,
			onSelectionChange,
			selection,
			maxLength,
		});

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
			paddingRight={8}
			flexWrap="wrap"
			rowGap={8}
			borderTopWidth={withTopBorder ? 1 : 0}
			borderBottomWidth={withBottomBorder ? 1 : 0}
			borderTopColor={theme.colors.outline}
			borderBottomColor={theme.colors.outline}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				flex={hideChip ? 1 : undefined}
			>
				<ToolbarButton
					icon="content-copy"
					label="Copy"
					onPress={handleCopy}
					disabled={disabled || !canCopy}
					showDivider
					fullWidth={hideChip}
				/>
				<ToolbarButton
					icon="content-cut"
					label="Cut"
					onPress={handleCut}
					disabled={disabled || !canCut}
					showDivider
					fullWidth={hideChip}
				/>
				<ToolbarButton
					icon="content-paste"
					label="Paste"
					onPress={handlePaste}
					disabled={disabled}
					showDivider={!hideChip}
					fullWidth={hideChip}
				/>
			</Box>
			{hideChip ? null : (
				<WordCountChip
					wordCount={wordCount}
					wordLimit={wordLimit}
					pluralWord={pluralWord}
					overrideText={wordCountText}
				/>
			)}
		</Box>
	);
};
